// InSkill Employee & Work Reporting Service
// Clean local storage persistence with zero dummy/seed data

export const INITIAL_EMPLOYEES = [
  { id: 'INSK-001', name: 'Ismail', role: 'Intern', pin: '1234' },
  { id: 'INSK-002', name: 'Pooja', role: 'Intern', pin: '1234' },
  { id: 'INSK-003', name: 'Ayesha', role: 'Intern', pin: '1234' },
  { id: 'INSK-004', name: 'Ananshi', role: 'Intern', pin: '1234' },
];

export const COMMON_TIMEZONES = [
  { label: 'Asia/Kolkata (IST, UTC+5:30)', value: 'Asia/Kolkata' },
  { label: 'America/New_York (EDT/EST, UTC-4/-5)', value: 'America/New_York' },
  { label: 'America/Los_Angeles (PDT/PST, UTC-7/-8)', value: 'America/Los_Angeles' },
  { label: 'America/Chicago (CDT/CST, UTC-5/-6)', value: 'America/Chicago' },
  { label: 'Europe/London (BST/GMT, UTC+1/+0)', value: 'Europe/London' },
  { label: 'Europe/Berlin (CEST/CET, UTC+2/+1)', value: 'Europe/Berlin' },
  { label: 'Asia/Dubai (GST, UTC+4)', value: 'Asia/Dubai' },
  { label: 'Asia/Singapore (SGT, UTC+8)', value: 'Asia/Singapore' },
  { label: 'Australia/Sydney (AEST, UTC+10)', value: 'Australia/Sydney' },
  { label: 'UTC (Coordinated Universal Time)', value: 'UTC' }
];

const EMPLOYEES_STORAGE_KEY = 'inskill_employees_v1';
const REPORTS_STORAGE_KEY = 'inskill_work_reports_v1';
const AUTH_SESSION_KEY = 'inskill_employee_auth_session';

// Helper to format date YYYY-MM-DD
export function formatDate(date) {
  const d = new Date(date);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  return `${year}-${month}-${day}`;
}

// Get all employees (preserves user-added employees in localStorage)
export function getEmployees() {
  try {
    const data = localStorage.getItem(EMPLOYEES_STORAGE_KEY);
    if (!data) {
      localStorage.setItem(EMPLOYEES_STORAGE_KEY, JSON.stringify(INITIAL_EMPLOYEES));
      return INITIAL_EMPLOYEES;
    }
    const parsed = JSON.parse(data);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.error('Error reading employees:', e);
    return [];
  }
}

// Save employees
export function saveEmployees(employees) {
  localStorage.setItem(EMPLOYEES_STORAGE_KEY, JSON.stringify(employees));
}

// Add new employee
export function addEmployee({ id, name, role, pin }) {
  const employees = getEmployees();
  const cleanId = id.trim().toUpperCase();
  if (employees.some(e => e.id === cleanId)) {
    throw new Error(`Employee ID ${cleanId} already exists`);
  }
  const newEmp = {
    id: cleanId,
    name: name.trim(),
    role: (role || 'Intern').trim(),
    pin: (pin || '1234').trim()
  };
  employees.push(newEmp);
  saveEmployees(employees);
  return newEmp;
}

// Delete employee
export function deleteEmployee(id) {
  const employees = getEmployees().filter(e => e.id !== id);
  saveEmployees(employees);
}

// Verify employee credentials
export function verifyEmployee(id, pin) {
  const employees = getEmployees();
  const cleanId = (id || '').trim().toUpperCase();
  const cleanPin = (pin || '').trim();
  const found = employees.find(e => e.id === cleanId && e.pin === cleanPin);
  return found || null;
}

// Session auth for reporting page
export function getAuthenticatedEmployee() {
  try {
    const session = localStorage.getItem(AUTH_SESSION_KEY);
    if (!session) return null;
    return JSON.parse(session);
  } catch {
    return null;
  }
}

export function setAuthenticatedEmployee(employee) {
  localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(employee));
}

export function clearAuthenticatedEmployee() {
  localStorage.removeItem(AUTH_SESSION_KEY);
}

// Get all reports - NO SEED DATA, automatically strips any legacy dummy reports
export function getAllReports() {
  try {
    const data = localStorage.getItem(REPORTS_STORAGE_KEY);
    if (!data) {
      localStorage.setItem(REPORTS_STORAGE_KEY, JSON.stringify([]));
      return [];
    }
    const parsed = JSON.parse(data);
    if (!Array.isArray(parsed)) return [];
    
    // Purge any legacy dummy/seed data if present from earlier test
    const realReports = parsed.filter(r => r && !r.id?.startsWith('seed_'));
    if (realReports.length !== parsed.length) {
      localStorage.setItem(REPORTS_STORAGE_KEY, JSON.stringify(realReports));
    }
    return realReports;
  } catch (e) {
    console.error('Error reading reports:', e);
    return [];
  }
}

// Reset / Clear all reports
export function clearAllReports() {
  localStorage.setItem(REPORTS_STORAGE_KEY, JSON.stringify([]));
  return [];
}

// Save or update report
export function saveReport(reportData) {
  const reports = getAllReports();
  const cleanId = reportData.id || `rep_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
  
  const existingIdx = reports.findIndex(
    r => r.id === cleanId || (r.employeeId === reportData.employeeId && r.date === reportData.date)
  );

  const newRecord = {
    ...reportData,
    id: existingIdx >= 0 ? reports[existingIdx].id : cleanId,
    hoursWorked: parseFloat(reportData.hoursWorked) || 0,
    updatedAt: new Date().toISOString()
  };

  if (!newRecord.createdAt) {
    newRecord.createdAt = new Date().toISOString();
  }

  if (existingIdx >= 0) {
    reports[existingIdx] = newRecord;
  } else {
    reports.unshift(newRecord);
  }

  localStorage.setItem(REPORTS_STORAGE_KEY, JSON.stringify(reports));
  return newRecord;
}

// Delete report
export function deleteReport(id) {
  const reports = getAllReports().filter(r => r.id !== id);
  localStorage.setItem(REPORTS_STORAGE_KEY, JSON.stringify(reports));
}

// Get reports for a single employee
export function getReportsForEmployee(employeeId) {
  const reports = getAllReports();
  return reports
    .filter(r => r.employeeId === employeeId)
    .sort((a, b) => new Date(b.date) - new Date(a.date));
}

// Calculate week range (Monday to Sunday) for a given date
export function getWeekRange(dateInput = new Date()) {
  const d = new Date(dateInput);
  const day = d.getDay(); // 0 is Sunday
  const diffToMonday = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diffToMonday));
  monday.setHours(0, 0, 0, 0);

  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);

  const days = [];
  for (let i = 0; i < 7; i++) {
    const cur = new Date(monday);
    cur.setDate(monday.getDate() + i);
    days.push(formatDate(cur));
  }

  return {
    monday,
    sunday,
    mondayStr: formatDate(monday),
    sundayStr: formatDate(sunday),
    days
  };
}

// Export reports as CSV
export function exportReportsToCsv(reports) {
  const headers = ['Report ID', 'Date', 'Employee ID', 'Employee Name', 'Role', 'Hours Worked', 'Timezone', 'Tasks / Work Summary', 'Notes'];
  const rows = reports.map(r => [
    `"${r.id || ''}"`,
    `"${r.date || ''}"`,
    `"${r.employeeId || ''}"`,
    `"${(r.employeeName || '').replace(/"/g, '""')}"`,
    `"${(r.role || '').replace(/"/g, '""')}"`,
    r.hoursWorked || 0,
    `"${r.timezone || ''}"`,
    `"${(r.tasksDone || '').replace(/"/g, '""')}"`,
    `"${(r.notes || '').replace(/"/g, '""')}"`
  ]);

  const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement('a');
  link.setAttribute('href', encodedUri);
  link.setAttribute('download', `InSkill_Work_Reports_${formatDate(new Date())}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
